/**
 * @file     listingEntrySource.ts
 * @purpose  Classify standalone listing-page arrivals as internal or direct/external.
 */

export const LISTING_ENTRY_EVENT = 'relta:listing-entry-classified';

const PENDING_INTERNAL_KEY = 'relta.gate.pending_internal_listing';
const CURRENT_ENTRY_KEY = 'relta.gate.current_listing_entry';
const PENDING_TTL_MS = 30_000;
const RESERVED_PROPERTY_ROUTES = new Set(['saved', 'vow', 'neighbourhood']);

type StoredEntry = {
  pathname: string;
  internal: boolean;
  at: number;
};

type PendingEntry = {
  pathname: string;
  at: number;
};

export type ListingEntryEvidence = {
  pathname: string;
  origin: string;
  referrer: string;
  now: number;
  navigationType?: string;
  pending?: PendingEntry | null;
  stored?: StoredEntry | null;
};

export function isStandaloneListingPath(pathname: string): boolean {
  if (!pathname.startsWith('/properties/')) return false;
  const segment = pathname.slice('/properties/'.length).split('/')[0];
  return Boolean(segment) && !RESERVED_PROPERTY_ROUTES.has(segment);
}

export function classifyListingEntryEvidence(evidence: ListingEntryEvidence): boolean {
  if (
    evidence.pending?.pathname === evidence.pathname
    && evidence.now - evidence.pending.at >= 0
    && evidence.now - evidence.pending.at <= PENDING_TTL_MS
  ) {
    return true;
  }

  if (
    evidence.navigationType === 'reload'
    && evidence.stored?.pathname === evidence.pathname
  ) {
    return evidence.stored.internal;
  }

  if (!evidence.referrer) return false;
  try {
    return new URL(evidence.referrer).origin === evidence.origin;
  } catch {
    return false;
  }
}

export function canApplyListingGate(
  surface: 'slug_page' | 'map_sheet' | undefined,
  isInternalEntry: boolean,
): boolean {
  return surface !== 'slug_page' || isInternalEntry;
}

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) as T : null;
  } catch {
    return null;
  }
}

function navigationType(): string | undefined {
  const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return entry?.type;
}

function publish(entry: StoredEntry): void {
  try {
    window.sessionStorage.setItem(CURRENT_ENTRY_KEY, JSON.stringify(entry));
  } catch {
    // Storage can be unavailable in privacy modes. The event still updates mounted gates.
  }
  window.dispatchEvent(new CustomEvent<StoredEntry>(LISTING_ENTRY_EVENT, { detail: entry }));
}

export function markInternalListingNavigation(href: string): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin || !isStandaloneListingPath(url.pathname)) return;
    const pending: PendingEntry = { pathname: url.pathname, at: Date.now() };
    window.localStorage.setItem(PENDING_INTERNAL_KEY, JSON.stringify(pending));
  } catch {
    // A malformed href or blocked storage must not interfere with navigation.
  }
}

export function installInternalListingClickTracking(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const onClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest<HTMLAnchorElement>('a[href]');
    if (anchor) markInternalListingNavigation(anchor.href);
  };
  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}

export function confirmInternalListingEntry(pathname: string): void {
  if (typeof window === 'undefined' || !isStandaloneListingPath(pathname)) return;
  // Leave the same short-lived evidence used by full-page and new-tab clicks.
  // This makes the result independent of React parent/child effect order.
  markInternalListingNavigation(pathname);
  publish({ pathname, internal: true, at: Date.now() });
}

export function resolveListingEntry(pathname: string): boolean {
  if (typeof window === 'undefined' || !isStandaloneListingPath(pathname)) return false;
  const pending = readJson<PendingEntry>(window.localStorage, PENDING_INTERNAL_KEY);
  const stored = readJson<StoredEntry>(window.sessionStorage, CURRENT_ENTRY_KEY);
  const internal = classifyListingEntryEvidence({
    pathname,
    origin: window.location.origin,
    referrer: document.referrer,
    now: Date.now(),
    navigationType: navigationType(),
    pending,
    stored,
  });

  if (pending?.pathname === pathname) {
    try { window.localStorage.removeItem(PENDING_INTERNAL_KEY); } catch { /* best effort */ }
  }
  publish({ pathname, internal, at: Date.now() });
  return internal;
}

export function readCurrentListingEntry(pathname: string): boolean {
  if (typeof window === 'undefined') return false;
  const entry = readJson<StoredEntry>(window.sessionStorage, CURRENT_ENTRY_KEY);
  return entry?.pathname === pathname && entry.internal;
}
